import { useEffect, useRef, useState } from 'react';

import { useAxios } from 'configs/requests';
import { ShippingSpeed } from 'constants/enums';
import { isAddressValid } from 'utils/Address';

interface IUseShippingOptionsArgs {
  gid: string;
  /** The address as the form holds it; nothing is requested until it is complete. */
  address: IAddress | null | undefined;
  /** The order's shipping block, built the same way the order payload builds it. */
  shippingInfo: IDeviceOrderShippingInfo;
  items: IBulkDeviceOrderItem[];
  /** False hides the whole feature (no Amazon SKU in the cart); nothing is requested. */
  enabled: boolean;
}

interface IUseShippingOptionsResult {
  options: IShippingOption[] | undefined;
  loading: boolean;
}

const DEBOUNCE_MS = 500;

/**
 * Both speeds are always offered and the preview only tells us when Amazon cannot keep one. When
 * the preview itself fails, the cards stay selectable and say "Estimate unavailable": ordering is
 * never blocked by an estimate (spec 2.2).
 */
export const FALLBACK_SHIPPING_OPTIONS: IShippingOption[] = [
  { speed: ShippingSpeed.STANDARD, available: true, estimatedDelivery: null },
  { speed: ShippingSpeed.EXPEDITED, available: true, estimatedDelivery: null }
];

/**
 * The Amazon fulfillment preview for the address being typed: which speeds are available and when
 * each would arrive.
 *
 * Keyed on what decides the answer, the group, the address and the items, and debounced 500 ms on
 * that key: a street typed letter by letter asks once, and a phone number or a contact name typed
 * afterwards does not ask Amazon again. The full shipping block is read from a ref when the timer
 * fires, so the request still carries the contact as it is at that moment. An address edited back
 * to one already answered within the debounce is not re-asked either. `loading` covers the debounce
 * wait too: from the moment the key changes until the answer for it is in, the cards show a
 * placeholder rather than the previous address's dates.
 */
const useShippingOptions = ({
  gid,
  address,
  shippingInfo,
  items,
  enabled
}: IUseShippingOptionsArgs): IUseShippingOptionsResult => {
  const [answeredBody, setAnsweredBody] = useState<string | null>(null);
  const [options, setOptions] = useState<IShippingOption[] | undefined>(undefined);
  const [{ loading }, fetchOptions] = useAxios<IShippingOptionsResponse, IShippingOptionsRequest>(
    { url: '/clinician-gateway/device/order/shipping-options', method: 'POST' },
    { manual: true }
  );
  const shippingInfoRef = useRef(shippingInfo);
  shippingInfoRef.current = shippingInfo;

  const ready = enabled && !!gid && items.length > 0 && !!isAddressValid(address);
  const body = ready ? JSON.stringify({ gid, address: shippingInfo.address, items }) : '';

  useEffect(() => {
    if (!body || answeredBody === body) return undefined;
    const handle = setTimeout(() => {
      const { gid: requestGid, items: requestItems } = JSON.parse(body) as {
        gid: string;
        items: IBulkDeviceOrderItem[];
      };
      const data: IShippingOptionsRequest = {
        gid: requestGid,
        shippingInfo: shippingInfoRef.current,
        items: requestItems
      };
      fetchOptions({ data })
        .then(({ data: response }) => {
          setOptions(response.options?.length ? response.options : FALLBACK_SHIPPING_OPTIONS);
          setAnsweredBody(body);
        })
        .catch(() => {
          setOptions(FALLBACK_SHIPPING_OPTIONS);
          setAnsweredBody(body);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [body, answeredBody, fetchOptions]);

  if (!ready) return { options: undefined, loading: false };

  const stale = answeredBody !== body;
  return { options: stale ? undefined : options, loading: stale || loading };
};

export default useShippingOptions;
